import React, { useEffect, useRef, useState } from 'react';
import {
  formatGroupedNumber,
  formatGroupedNumberInput,
  isPartialGroupedNumber,
  parseGroupedNumber,
} from '../../utils/formatters';

export interface AmountInputProps {
  id?: string;
  name?: string;
  label?: React.ReactNode;
  value: number | string | null | undefined;
  /** Emits a finite number, or undefined when cleared (allowEmpty). */
  onChange: (value: number | undefined) => void;
  required?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
  error?: string;
  className?: string;
  inputClassName?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  /** Shown on the left inside the field (e.g. "R"). */
  prefix?: string;
  /** Decimal places applied on blur. Default 2. */
  fractionDigits?: number;
  /** When true, commit integers only on blur. */
  integer?: boolean;
  autoFocus?: boolean;
}

function toDisplay(
  value: number | string | null | undefined,
  fractionDigits: number | undefined,
  blurFormat: boolean,
): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseGroupedNumber(String(value));
  if (num === undefined || Number.isNaN(num)) return '';

  if (blurFormat && fractionDigits !== undefined) {
    return formatGroupedNumber(num, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  return formatGroupedNumber(num, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
  });
}

export default function AmountInput({
  id,
  name,
  label,
  value,
  onChange,
  required = false,
  min,
  max,
  placeholder = '0.00',
  error,
  className = '',
  inputClassName = '',
  allowEmpty = false,
  disabled = false,
  prefix,
  fractionDigits = 2,
  integer = false,
  autoFocus = false,
}: AmountInputProps) {
  const [displayValue, setDisplayValue] = useState(() =>
    toDisplay(value, fractionDigits, true),
  );
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDisplayValue(toDisplay(value, fractionDigits, true));
    }
  }, [value, fractionDigits]);

  const commit = (raw: string, formatBlur: boolean) => {
    if (isPartialGroupedNumber(raw) || raw.trim() === '') {
      if (allowEmpty) {
        if (formatBlur) setDisplayValue('');
        onChange(undefined);
      } else {
        const fallback = min ?? 0;
        if (formatBlur) {
          setDisplayValue(toDisplay(fallback, fractionDigits, true));
        }
        onChange(fallback);
      }
      return;
    }

    let next = parseGroupedNumber(raw);
    if (next === undefined) {
      setDisplayValue(toDisplay(value, fractionDigits, true));
      return;
    }

    if (integer) next = Math.floor(next);
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);

    if (formatBlur) {
      setDisplayValue(toDisplay(next, fractionDigits, true));
    } else {
      setDisplayValue(formatGroupedNumberInput(raw));
    }
    onChange(next);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw !== '' && !/^-?[\d,\s]*\.?\d*$/.test(raw)) return;

    const formatted = formatGroupedNumberInput(raw);
    setDisplayValue(formatted);

    if (isPartialGroupedNumber(formatted)) return;

    const parsed = parseGroupedNumber(formatted);
    if (parsed === undefined) return;
    if (min !== undefined && parsed < min) return;
    if (max !== undefined && parsed > max) return;
    onChange(integer ? Math.floor(parsed) : parsed);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    focusedRef.current = true;
    e.target.select();
  };

  const handleBlur = () => {
    focusedRef.current = false;
    commit(displayValue, true);
  };

  const defaultInputClass = prefix
    ? `block w-full input border-gray-300 shadow-sm sm:text-sm pl-9 ${error ? 'border-red-300' : ''}`
    : `${label ? 'mt-1 ' : ''}block w-full input border-gray-300 shadow-sm sm:text-sm ${error ? 'border-red-300' : ''}`;

  const inputEl = (
    <input
      id={id}
      name={name}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      className={inputClassName || defaultInputClass}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      aria-invalid={error ? true : undefined}
    />
  );

  const input = prefix ? (
    <div className={label ? 'mt-1 relative rounded-md shadow-sm' : 'relative rounded-md shadow-sm'}>
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <span className="text-slate-500 sm:text-sm">{prefix}</span>
      </div>
      {inputEl}
    </div>
  ) : (
    inputEl
  );

  if (!label) return input;

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700" htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {input}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
